import { Layers, CheckCircle2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type Props = {
  allTypes: string[];
  selectedTypes: string[];
  setSelectedTypes: React.Dispatch<React.SetStateAction<string[]>>;
};

const TypeSelection = ({
  allTypes,
  selectedTypes,
  setSelectedTypes,
}: Props) => {
  const toggleTypeSelection = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const selectAll = () => {
    setSelectedTypes(allTypes);
  };

  const clearAll = () => {
    setSelectedTypes([]);
  };

  const getTypeColor = (type: string) => {
    const trimmedType = type.trim();
    if (trimmedType === 'بيع') return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400';
    if (trimmedType === 'مرتجع عميل') return 'bg-orange-500/10 border-orange-500/30 text-orange-600 dark:text-orange-400';
    if (trimmedType === 'شراء') return 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400';
    if (trimmedType === 'مرتجع مورد') return 'bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400';
    return 'bg-gray-500/10 border-gray-500/30 text-gray-600 dark:text-gray-400';
  };

  const getTypeIcon = (type: string) => {
    const trimmedType = type.trim();
    if (trimmedType === 'بيع') return '💰';
    if (trimmedType === 'مرتجع عميل') return '↩️';
    if (trimmedType === 'شراء') return '📦';
    if (trimmedType === 'مرتجع مورد') return '🔄';
    return '📋';
  };

  if (!allTypes.length) return null;

  return (
    <Card className="w-full border-0 bg-gradient-to-br from-muted/30 to-muted/10 p-3">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-primary/10 p-1.5">
              <Layers className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">نوع الحركة</h3>
              <p className="text-xs text-muted-foreground">
                {allTypes.length} أنواع متاحة
              </p>
            </div>
          </div>
          
          {selectedTypes.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge 
                variant="outline" 
                className="bg-primary/5 border-primary/20 text-primary text-xs"
              >
                {selectedTypes.length} محدد
              </Badge>
              {selectedTypes.length === allTypes.length ? (
                <button
                  onClick={clearAll}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  إلغاء الكل
                </button>
              ) : (
                <button
                  onClick={selectAll}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  تحديد الكل
                </button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2">
          {allTypes.map((type) => {
            const isSelected = selectedTypes.includes(type);
            return (
              <div
                key={type}
                className={cn(
                  "relative flex items-center gap-3 rounded-lg px-3 py-2.5 transition-all cursor-pointer",
                  "border border-border/50 bg-background/30",
                  "hover:bg-muted/50 dark:hover:bg-muted/30",
                  isSelected && "bg-primary/5 border-primary/30 shadow-sm"
                )}
                onClick={() => toggleTypeSelection(type)}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleTypeSelection(type)}
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  onClick={(e) => e.stopPropagation()}
                />
                
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-lg">{getTypeIcon(type)}</span>
                  <span className={cn(
                    "text-sm",
                    isSelected && "font-medium"
                  )}>
                    {type}
                  </span>
                </div>
                
                <Badge 
                  variant="outline" 
                  className={cn(
                    "h-5 px-1.5 text-[10px]",
                    getTypeColor(type)
                  )}
                >
                  {type.trim() === 'بيع' && 'Sale'}
                  {type.trim() === 'مرتجع عميل' && 'Return'}
                  {type.trim() === 'شراء' && 'Purchase'}
                  {type.trim() === 'مرتجع مورد' && 'Refund'}
                </Badge>

                {isSelected && (
                  <CheckCircle2 className="absolute top-1 left-1 h-3.5 w-3.5 text-primary animate-in zoom-in-50 duration-200" />
                )}
              </div>
            );
          })}
        </div>

        {selectedTypes.length > 0 && selectedTypes.length < allTypes.length && (
          <div className="pt-2 border-t border-border/30">
            <div className="flex flex-wrap gap-1">
              {selectedTypes.map((type) => (
                <Badge 
                  key={type}
                  variant="outline"
                  className={cn(
                    "text-xs px-2 py-0.5",
                    getTypeColor(type)
                  )}
                >
                  {getTypeIcon(type)} {type}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default TypeSelection;